/**
 * Pure anchor/range math behind useMultiSelect (hooks/useMultiSelect.ts).
 * Split out so the shift-click range logic — the part worth pinning with
 * tests — has no React dependency.
 */

export interface RangeToggleResult {
  /** The new selection. A fresh Set; the input `selected` is left untouched. */
  next: Set<string>;
  /** The anchor for the *next* shift-click. `null` clears it. */
  nextAnchor: string | null;
}

/**
 * One click's worth of selection math, in the list's CURRENT rendered order
 * (`orderedIds` — callers must re-derive this after every filter/sort so a
 * stale order never leaks in).
 *
 * - Disabled `id`: no-op, anchor untouched. Disabled rows are never toggled
 *   by a direct click, and are never a valid shift-click target either.
 * - Plain click (no shift, or shift with no anchor yet — "shift-click with
 *   no anchor = plain toggle"): flips just `id` and makes it the new anchor.
 * - Shift-click with an anchor still present in `orderedIds`: SETS every
 *   non-disabled id between anchor and target to selected (Gmail semantics —
 *   never blind-toggles each row), and moves the anchor to `id`.
 * - Shift-click whose anchor fell out of `orderedIds` (the list re-sorted or
 *   was filtered since the anchor was set): falls back to a plain toggle
 *   rather than ranging across a gap the teacher never saw.
 */
export function computeRangeToggle(
  orderedIds: readonly string[],
  disabledIds: ReadonlySet<string>,
  selected: ReadonlySet<string>,
  anchor: string | null,
  id: string,
  shiftKey: boolean,
): RangeToggleResult {
  if (disabledIds.has(id)) {
    return { next: new Set(selected), nextAnchor: anchor };
  }

  if (shiftKey && anchor !== null && anchor !== id) {
    const anchorIdx = orderedIds.indexOf(anchor);
    const targetIdx = orderedIds.indexOf(id);
    if (anchorIdx !== -1 && targetIdx !== -1) {
      const [lo, hi] = anchorIdx < targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
      const next = new Set(selected);
      for (let i = lo; i <= hi; i++) {
        const rowId = orderedIds[i]!;
        if (!disabledIds.has(rowId)) next.add(rowId);
      }
      return { next, nextAnchor: id };
    }
    // Anchor no longer in the visible order — fall through to a plain toggle.
  }

  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return { next, nextAnchor: id };
}

/** True when two ordered id lists are the same sequence (used to decide
 *  whether the rendered order changed and the anchor should drop). */
export function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
